import React, { useState } from 'react';
import { 
  FileText, 
  CreditCard, 
  CheckCircle, 
  RotateCcw, 
  Download, 
  ChevronDown,
  X,
  Loader2
} from 'lucide-react';
import { BillingEvent, formatCurrency, getStatusBadge, getTypeBadge } from './billingTypes';

interface BillingEventsTableProps {
  events: BillingEvent[];
  billingProfiles: { id: number; name: string }[];
  workspaces: { id: number; name: string }[];
  filters: {
    billingProfileId: number | null;
    workspaceId: number | null;
    type: string | null;
    status: string | null;
    from: string | null;
    to: string | null;
  };
  onFilterChange: (filters: any) => void;
  onDownload: (eventId: number) => void;
  loading?: boolean;
  downloadingId?: number | null;
}

const BillingEventsTable: React.FC<BillingEventsTableProps> = ({
  events,
  billingProfiles,
  workspaces,
  filters,
  onFilterChange,
  onDownload,
  loading = false,
  downloadingId = null,
}) => {
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const typeOptions = [
    { value: null, label: 'All Types' },
    { value: 'invoice', label: 'Invoices' },
    { value: 'credit_note', label: 'Credits' },
    { value: 'payment', label: 'Payments' },
  ];

  const statusOptions = [
    { value: null, label: 'All Statuses' },
    { value: 'paid,success', label: 'Paid/Success' },
    { value: 'pending,payment_due', label: 'Pending' },
    { value: 'failed,not_paid', label: 'Failed' },
    { value: 'refunded,voided', label: 'Refunded/Voided' },
  ];

  const getTypeIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      invoice: <FileText size={14} strokeWidth={1.5} />,
      credit_note: <CreditCard size={14} strokeWidth={1.5} />,
      payment: <CheckCircle size={14} strokeWidth={1.5} />,
      refund: <RotateCcw size={14} strokeWidth={1.5} />,
    };
    return icons[type] || <FileText size={14} strokeWidth={1.5} />;
  };

const hasActiveFilters = filters.billingProfileId || filters.workspaceId || filters.type || filters.status || filters.from || filters.to;

  const clearFilters = () => {
    onFilterChange({ billingProfileId: null, workspaceId: null, type: null, status: null, from: null, to: null });
  };

  // Dropdown component
  const Dropdown: React.FC<{
    id: string;
    label: string;
    value: string;
    options: { value: any; label: string }[];
    onChange: (value: any) => void;
  }> = ({ id, label, value, options, onChange }) => (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpenDropdown(openDropdown === id ? null : id);
        }}
        style={{
          padding: '8px 12px',
          backgroundColor: 'var(--v2-surface)',
          border: '1px solid var(--v2-border)',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.8125rem',
          color: 'var(--v2-text)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: '140px',
          justifyContent: 'space-between',
        }}
      >
        <span>{value}</span>
        <ChevronDown
          size={14}
          strokeWidth={1.5}
          color="var(--v2-text-muted)"
          style={{
            transition: 'transform 0.15s ease',
            transform: openDropdown === id ? 'rotate(180deg)' : 'rotate(0)',
          }}
        />
      </button>
      {openDropdown === id && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            backgroundColor: 'var(--v2-surface)',
            border: '1px solid var(--v2-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 20,
            minWidth: '180px',
            maxHeight: '240px',
            overflowY: 'auto',
            padding: '4px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {options.map((opt) => (
            <button
              key={opt.value || 'all'}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpenDropdown(null);
              }}
              style={{
                padding: '8px 10px',
                fontSize: '0.8125rem',
                color: 'var(--v2-text)',
                backgroundColor: 'transparent',
                border: 'none',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'block',
                borderRadius: 'var(--radius-sm)',
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Close dropdowns on outside click
  React.useEffect(() => {
    const handleClick = () => setOpenDropdown(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return (
    <div
      style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--v2-border)',
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
      }}
    >
      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          padding: '16px 20px',
          borderBottom: '1px solid var(--v2-border)',
          backgroundColor: 'var(--v2-surface-alt)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: 'var(--v2-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Filter by:
        </span>

        <Dropdown
          id="profile"
          label="Billing Account"
          value={
            filters.billingProfileId
              ? billingProfiles.find((p) => p.id === filters.billingProfileId)?.name || 'All Accounts'
              : 'All Accounts'
          }
          options={[
            { value: null, label: 'All Accounts' },
            ...billingProfiles.map((p) => ({ value: p.id, label: p.name })),
          ]}
          onChange={(value) => onFilterChange({ ...filters, billingProfileId: value })}
        />

        <Dropdown
          id="workspace"
          label="Workspace"
          value={
            filters.workspaceId
              ? workspaces.find((w) => w.id === filters.workspaceId)?.name || 'All Workspaces'
              : 'All Workspaces'
          }
          options={[
            { value: null, label: 'All Workspaces' },
            ...workspaces.map((w) => ({ value: w.id, label: w.name })),
          ]}
          onChange={(value) => onFilterChange({ ...filters, workspaceId: value })}
        />

        <Dropdown
          id="type"
          label="Type"
          value={typeOptions.find((t) => t.value === filters.type)?.label || 'All Types'}
          options={typeOptions}
          onChange={(value) => onFilterChange({ ...filters, type: value })}
        />

        <Dropdown
          id="status"
          label="Status"
          value={statusOptions.find((s) => s.value === filters.status)?.label || 'All Statuses'}
          options={statusOptions}
          onChange={(value) => onFilterChange({ ...filters, status: value })}
        />

{/* Date Range */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--v2-text-muted)' }}>From:</span>
          <input
            type="date"
            value={filters.from || ''}
            onChange={(e) => onFilterChange({ ...filters, from: e.target.value || null })}
            style={{
              padding: '8px 10px',
              backgroundColor: 'var(--v2-surface)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.8125rem',
              color: 'var(--v2-text)',
              cursor: 'pointer',
            }}
          />
          <span style={{ fontSize: '0.75rem', color: 'var(--v2-text-muted)' }}>To:</span>
          <input
            type="date"
            value={filters.to || ''}
            onChange={(e) => onFilterChange({ ...filters, to: e.target.value || null })}
            style={{
              padding: '8px 10px',
              backgroundColor: 'var(--v2-surface)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.8125rem',
              color: 'var(--v2-text)',
              cursor: 'pointer',
            }}
          />
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 12px',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.8125rem',
              color: 'var(--v2-primary)',
              cursor: 'pointer',
              fontWeight: 500,
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X size={14} strokeWidth={1.5} />
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <Loader2
            size={32}
            strokeWidth={2}
            color="var(--v2-primary)"
            style={{ animation: 'spin 1s linear infinite', margin: '0 auto 16px' }}
          />
          <p style={{ color: 'var(--v2-text-tertiary)', margin: 0 }}>Loading billing history...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : events.length === 0 ? (
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              backgroundColor: 'var(--v2-surface-alt)',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <FileText size={24} strokeWidth={1.5} color="var(--v2-text-muted)" />
          </div>
          <p style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--v2-text)', marginBottom: '8px' }}>
            {hasActiveFilters ? 'No events match your filters' : 'No billing history yet'}
          </p>
          <p style={{ color: 'var(--v2-text-tertiary)', margin: 0 }}>
            {hasActiveFilters
              ? 'Try adjusting your filters to see more results'
              : 'Billing events will appear here once you have activity'}
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--v2-surface)' }}>
                {['Date', 'Type', 'Description', 'Billing Account', 'Workspace', 'Amount', 'Status', ''].map(
                  (header, i) => (
                    <th
                      key={header || i}
                      style={{
                        padding: '12px 16px',
                        textAlign: header === 'Amount' ? 'right' : 'left',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        color: 'var(--v2-text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid var(--v2-border)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {events.map((event) => {
                const typeBadge = getTypeBadge(event.type);
                const statusBadge = getStatusBadge(event.status);
                const isDownloadable = ['invoice', 'credit_note'].includes(event.type);
                const isDownloading = downloadingId === event.id;

                return (
                  <tr
                    key={event.id}
                    style={{
                      backgroundColor: hoveredRow === event.id ? 'var(--v2-surface-hover)' : 'transparent',
                      transition: 'background-color 0.1s ease',
                    }}
                    onMouseEnter={() => setHoveredRow(event.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <td
                      style={{
                        padding: '14px 16px',
                        fontSize: '0.8125rem',
                        color: 'var(--v2-text-secondary)',
                        borderBottom: '1px solid var(--v2-border)',
                        fontFamily: 'var(--font-mono)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {new Date(event.event_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td style={{ padding: '14px 16px', borderBottom: '1px solid var(--v2-border)' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: typeBadge.bg,
                          color: typeBadge.text,
                          borderRadius: 'var(--radius-full)',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {getTypeIcon(event.type)}
                        {typeBadge.label}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '14px 16px',
                        fontSize: '0.875rem',
                        color: 'var(--v2-text)',
                        borderBottom: '1px solid var(--v2-border)',
                        maxWidth: '250px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={event.description || event.chargebee_id}
                    >
                      {event.description || event.chargebee_id}
                    </td>
                    <td
                      style={{
                        padding: '14px 16px',
                        fontSize: '0.875rem',
                        color: 'var(--v2-text)',
                        borderBottom: '1px solid var(--v2-border)',
                      }}
                    >
                      {event.billingProfile?.name || '—'}
                    </td>
                    <td
                      style={{
                        padding: '14px 16px',
                        fontSize: '0.875rem',
                        color: 'var(--v2-text-secondary)',
                        borderBottom: '1px solid var(--v2-border)',
                      }}
                    >
                      {event.workspace?.name || '—'}
                    </td>
                    <td
                      style={{
                        padding: '14px 16px',
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        color: event.type === 'refund' || event.type === 'credit_note' 
                          ? 'var(--v2-success-dark)' 
                          : 'var(--v2-text)',
                        textAlign: 'right',
                        borderBottom: '1px solid var(--v2-border)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {event.type === 'refund' || event.type === 'credit_note' ? '+' : ''}
                      {formatCurrency(event.amount_cents, event.currency)}
                    </td>
                    <td style={{ padding: '14px 16px', borderBottom: '1px solid var(--v2-border)' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          backgroundColor: statusBadge.bg,
                          color: statusBadge.text,
                          borderRadius: 'var(--radius-full)',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}
                      >
                        {statusBadge.label}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', borderBottom: '1px solid var(--v2-border)' }}>
                      {isDownloadable && (
                        <button
                          onClick={() => onDownload(event.id)}
                          disabled={isDownloading}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '6px 10px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: isDownloading ? 'wait' : 'pointer',
                            color: 'var(--v2-primary)',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            opacity: isDownloading ? 0.6 : 1,
                          }}
                          onMouseOver={(e) => {
                            if (!isDownloading) e.currentTarget.style.backgroundColor = 'var(--v2-primary-light)';
                          }}
                          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                          title="Download PDF"
                        >
                          {isDownloading ? (
                            <Loader2 size={14} strokeWidth={1.5} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <Download size={14} strokeWidth={1.5} />
                          )}
                          PDF
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BillingEventsTable;
